import validator from 'validator';
import { WidgetType } from 'util/propTypes';

const THIS = 'this_';

export function getRequestErrorMessage(request, t) {
  let message;
  if (request.json && request.json.code) {
    message = t(`error:${request.json.code}`, request.json.data);
    if (message === `Error:${request.json.code.toString()}`) {
      message = `${request.json.code}: ${request.json.message}`;
    }
  } else if (request.responseStatus) {
    message = t(`error:status.${request.responseStatus}`);
  } else {
    message = t('error:noResponse');
  }
  return { message };
}

export function getDottedId(id) {
  if (id.length > 9) {
    return `${id.slice(0, 9)}...`;
  }
  return id;
}

export function isEmail(str) {
  return validator.isEmail(str || '');
}

export function isUUID(str) {
  return validator.isUUID(str || '');
}

const SERVICES = ['network', 'device', 'value', 'state'];
export const operationRegex = /(==|=|\^=|\$=)/;
const toEqualOperationRegex = /(\^=|\$=)/;

const ADD_ATTRIBUTE_EXTRA = {
  connection: ['timestamp', 'online'],
  meta: ['iot', 'application', 'id', 'type'],
  action: ['meta.id', 'meta.type'],
};

const getPast = (number, unit) => {
  const date = new Date();
  switch (unit) {
    case 'week':
      date.setHours(date.getHours() - number * 7 * 24);
      break;
    case 'day':
      date.setHours(date.getHours() - number * 24);
      break;
    case 'hour':
      date.setHours(date.getHours() - number);
      break;
    case 'minute':
    default:
      date.setMinutes(date.getMinutes() - number);
      break;
  }
  return date.toISOString();
};

const getAttribute = (attribute) => {
  const ATTRIBUTES = {
    connection: 'meta.connection.timestamp',
    Report: '[this_type==Report]_data',
    Control: '[this_type==Control]_data',
  };
  return ATTRIBUTES[attribute] || attribute;
};

const getPathToService = (type, service) => {
  const serviceIndex = SERVICES.indexOf(service);
  const typeServiceIndex = SERVICES.indexOf(type);
  if (serviceIndex === -1 || typeServiceIndex === -1) {
    return THIS;
  }
  const diff = typeServiceIndex - serviceIndex;
  if (diff === 0) {
    return THIS;
  }
  if (diff > 0) {
    return `child_${Math.abs(diff)}_`;
  }
  return `parent_${Math.abs(diff)}_`;
};

const getFilterCondition = (filter) => {
  if (filter.attribute === 'connection') {
    switch (filter.status) {
      case 'hasChanged':
        return filter.status;
      default:
        return filter.condition;
    }
  }
  return filter.condition;
};

const getConditionAndValue = (condition, value, unit) => {
  switch (condition) {
    case 'contains':
      return ['=', value];
    case 'is':
      return ['==', value];
    case 'smallerThan':
      return ['<', value];
    case 'largerThan':
      return ['>', value];
    case 'past':
      return ['<=', getPast(value, unit)];
    case 'hasChanged':
      return ['>=', getPast(value, unit)];
    case 'now':
      return [];
    default:
      return ['=', value];
  }
};

export const getAlias = (service, key) => `alias_${service}_${key}`;

export const parseWidgetUrl = (
  widget: WidgetType,
  skipColumns?: boolean,
  skipExtraAttribute?: boolean,
): [string, string, { return: string }, string] => {
  let filterQuery = '';
  const { configuration } = widget;
  const service = configuration.url;
  const parentFilter = [];
  let gql: Record<string, any> = {};

  const addToObject = (obj, path) => {
    let prev = obj;
    const split = path.split('.');
    split.forEach((k, i) => {
      if (!prev[k]) {
        if (!skipExtraAttribute && ADD_ATTRIBUTE_EXTRA[k]) {
          prev[k] = {};
          ADD_ATTRIBUTE_EXTRA[k].forEach((attr) => addToObject(prev[k], attr));
        } else {
          prev[k] = i === split.length - 1 ? '' : {};
        }
      }
      prev = prev[k];
    });
  };

  const createGqlQuery = (obj) => {
    const shape = [];
    Object.entries(obj).forEach(([key, val]: [string, any]) => {
      // TODO: for (const [key, val] of Object.entries(obj)) {
      if (key !== 'filters') {
        let str = '';
        if (val) {
          str = `${key}`;
          if (val.filters) {
            let filterString = '';
            val.filters.forEach((f) => {
              filterString += `"${f.replace(toEqualOperationRegex, '=')}",`;
            });
            filterString = filterString.slice(0, -1);
            if (filterString) {
              str += `(attribute:[${filterString}])`;
            }
          }
          str += ` { ${createGqlQuery(val)} }`;
        } else {
          str = key;
        }
        shape.push(str);
      }
    });
    return shape.join(' ');
  };

  if (configuration.filters.length > 0) {
    // Maybe replace this code with filter to make it easier
    configuration.filters.forEach((filter) => {
      const pathToService = getPathToService(filter.type, service);
      const attribute = getAttribute(filter.attribute);
      const filterCondition = getFilterCondition(filter);
      /* eslint-disable-next-line prefer-const */
      let [condition, value] = getConditionAndValue(filterCondition, filter.value, filter.unit);
      if (condition) {
        if (
          attribute &&
          attribute.endsWith('data') &&
          !Number.isNaN(Number(value)) &&
          condition !== '='
        ) {
          value = `int(${value})`;
        }
        const filterString = `${attribute}${condition}${value}`;
        if (!parentFilter.find((f) => f.data === filterString && f.path === pathToService)) {
          filterQuery += `${pathToService + filterString}&`;
        }
      }
      if (
        filter.attribute === 'connection' &&
        filter.status !== 'hasChanged' &&
        filter.status !== 'hasNotChanged'
      ) {
        const extra = `${attribute.replace('timestamp', 'online')}=${filter.status === 'online'}`;
        if (!parentFilter.find((f) => f.data === extra && f.path === pathToService)) {
          parentFilter.push({
            data: extra,
            path: pathToService,
            skipAttribute: true,
          });
        }
        filterQuery += `${pathToService + extra}&`;
      }
    });
  }

  if (!skipColumns) {
    configuration.columns.forEach((column) => {
      if (!column.data) {
        return;
      }
      const keys = column.data.match(/[^[.]*(\[.*?\])?/g);
      let currentService = service;
      let prevAttribute = '';
      let currentGql = gql;
      keys.forEach((key) => {
        if (!key) {
          return;
        }
        const split = key.split('[');
        let filter;
        if (split.length > 1) {
          [currentService] = split;
          if (split[split.length - 1].includes('=')) {
            filter = split[split.length - 1];
          }
        }
        if (filter) {
          if (currentService !== service) {
            const filterString = filter.slice(0, -1);
            let path = THIS;
            if (filterString.startsWith('child_') || filterString.startsWith(THIS)) {
              path = '';
            }
            const filterSplit = filterString
              .replace(THIS, '')
              .replace(/child_([0-9]_)?/, '')
              .split(operationRegex);
            const filterKey = filterSplit[0];
            let gqlKey = getAlias(currentService, filterKey);
            gqlKey += `: ${currentService}`;
            if (!currentGql[gqlKey]) {
              currentGql[gqlKey] = {
                filters: [],
              };
            }
            currentGql = currentGql[gqlKey];
            const pathFilter = path + filterString;
            if (!currentGql.filters.includes(pathFilter)) {
              currentGql.filters.push(path + filterString);
            }
            if (filterString.startsWith(THIS) || path === THIS) {
              addToObject(currentGql, filterKey);
              if (!skipExtraAttribute && ADD_ATTRIBUTE_EXTRA[filterKey]) {
                ADD_ATTRIBUTE_EXTRA[filterKey].forEach((attr) => addToObject(currentGql, attr));
              }
            }
          }
          prevAttribute = '';
        } else {
          prevAttribute = prevAttribute ? `${prevAttribute}.${key}` : key;
        }
      });
      if (prevAttribute) {
        addToObject(currentGql, prevAttribute);
        if (!skipExtraAttribute && ADD_ATTRIBUTE_EXTRA[prevAttribute]) {
          ADD_ATTRIBUTE_EXTRA[prevAttribute].forEach((attr) => addToObject(currentGql, attr));
        }
        if (column.actionType) {
          ADD_ATTRIBUTE_EXTRA.action.forEach((attr) => addToObject(currentGql, attr));
        }
      }
    });
  }

  if (configuration.orderBy) {
    const pathToService = getPathToService(configuration.orderBy.type, service);
    const fromLast = configuration.orderBy.operation === 'descending';
    const { attribute: sortAttribute, type: sortType } = configuration.orderBy;

    function parsePathSegments(dataPath: string) {
      // "value[type=identifier].state[type==Report].data" -> ["value[type=identifier]", "state[type==Report]", "data"]
      return dataPath.match(/[^[.]*(\[.*?\])?/g)?.filter(Boolean) ?? [];
    }

    function getSegmentName(segment: string) {
      return segment.split('[')[0]; // "state[type==Report]" -> "state"
    }

    function getSegmentQualifier(segment: string) {
      return segment.match(/\[([^\]]+)\]/)?.[1] ?? null; // "state[type==Report]" -> "type==Report"
    }

    function getRelationPrefix(distanceFromTarget: number) {
      if (distanceFromTarget === 0) return 'this';
      if (distanceFromTarget === 1) return 'parent';
      return `parent_${distanceFromTarget}`;
    }

    function buildHierarchyFilter(segments: string[], targetIndex: number) {
      const parts: string[] = [];

      for (let i = 0; i <= targetIndex; i++) {
        const qualifier = getSegmentQualifier(segments[i]);
        if (!qualifier) continue;

        const prefix = getRelationPrefix(targetIndex - i);
        parts.push(`${prefix}_${qualifier}`);
      }

      return parts.length > 0 ? `[${parts.join('&')}]` : ''; // e.g "[parent_type=identifier&this_type==Report]"
    }

    function buildOrderByForColumn(column: any) {
      if (!column.data) return null;

      const segments = parsePathSegments(column.data);

      if (segments[segments.length - 1] !== sortAttribute) return null;

      const sortingTypeIndex = segments.findIndex(
        seg => getSegmentName(seg) === sortType
      );

      if (sortingTypeIndex === -1) return null;

      const hierarchyFilter = buildHierarchyFilter(segments, sortingTypeIndex);

      return hierarchyFilter
        ? `${hierarchyFilter}_${sortAttribute}`
        : sortAttribute;
    }

    let orderByAttribute = sortAttribute;

    for (const column of configuration.columns) {
      const result = buildOrderByForColumn(column);
      if (result !== null) {
        orderByAttribute = result;
        break;
      }
    }

    filterQuery += `order_by=${pathToService}${encodeURIComponent(orderByAttribute)}`;
    filterQuery += `&from_last=${fromLast}`;
  }

  gql = {
    [service]: gql,
  };
  if (!skipExtraAttribute) {
    addToObject(gql[service], 'meta');
  }
  const body = {
    return: `{ ${createGqlQuery(gql)} }`,
  };

  if (filterQuery.endsWith('&')) {
    filterQuery = filterQuery.slice(0, -1);
  }
  return [`/${service}`, filterQuery, body, service];
};

export const sToTime = (s) => {
  const SECOND = 'second';
  if (s < 60) return [s, SECOND];

  const MINUTE = 'minute';
  const minutes = s / 60;
  if (minutes < 60) return [minutes, MINUTE];

  const HOUR = 'hour';
  const hours = s / (60 * 60);
  if (hours < 24) return [hours, HOUR];

  const DAY = 'day';
  const days = s / (60 * 60 * 24);
  if (days < 7) return [days, DAY];

  const WEEK = 'week';
  const weeks = s / (60 * 60 * 24 * 7);
  if (weeks < 4) return [weeks, WEEK];

  const MONTH = 'month';
  const months = s / (60 * 60 * 24 * 7 * 4);
  return [months, MONTH];
};

export function getEnvironment() {
  let env;
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_HOST) {
    const host = process.env.REACT_APP_HOST;
    [, env] = host.split('://');
  } else {
    env = window.location.hostname;
  }

  if (env.startsWith('wappsto')) {
    env = 'prod';
  } else {
    [env] = env.split('.');
  }
  return env;
}

export function getItemName(item) {
  if (!item) return '';

  const name = item.meta?.name_by_user || item.name;
  return name;
}
